import React, {useEffect, useMemo, useState} from 'react';
import {Layout, Tabs, Tag} from "antd";
import {Card, Container, Text} from "@nextui-org/react";
import {useCollection} from "react-firebase-hooks/firestore";
import {collection, doc, getFirestore, orderBy, query, serverTimestamp, updateDoc} from "firebase/firestore";
import {getAuth} from "firebase/auth";
import {useToast} from "@chakra-ui/react";
import moment from 'moment';
import {Link} from "react-router-dom";
import Firebase from "../../Firebase/Firebase";
import Headerbar from "../../navBars/Headerbar";
import Sidebar from "../../navBars/Sidebar";
import SupportChats from "./SupportChats";
import SupportTickets from "./SupportTickets";
import SupportPayments from "./SupportPayments";

const db = getFirestore(Firebase);
const {Content} = Layout;
const {TabPane} = Tabs;

type Status = 'open' | 'replied' | 'closed';

const STATUS_COLORS: Record<Status, string> = {open: 'red', replied: 'blue', closed: 'default'};

function fmtDate(val: any) {
    if (!val) return '—';
    const d = typeof val.toDate === 'function' ? val.toDate() : val;
    return moment(d).format('DD MMM YYYY, HH:mm');
}

/** Contact left on the form → email / call / WhatsApp links. */
function contactLinks(contact: string) {
    const c = (contact || '').trim();
    if (/@/.test(c)) return [{label: 'Email', href: `mailto:${c}?subject=${encodeURIComponent('Re: your message to Shopped')}`}];
    const digits = c.replace(/[^\d+]/g, '');
    if (digits.replace(/\D/g, '').length < 7) return [];
    // Malawi numbers: 0XXXXXXXXX → 265XXXXXXXXX for WhatsApp.
    const intl = digits.startsWith('+') ? digits.slice(1) : digits.startsWith('0') ? `265${digits.slice(1)}` : digits;
    return [
        {label: 'Call', href: `tel:${digits}`},
        {label: 'WhatsApp', href: `https://wa.me/${intl}`},
    ];
}

const btn: React.CSSProperties = {
    fontSize: 12, border: '1px solid #c7d2fe', borderRadius: 6, padding: '4px 10px',
    cursor: 'pointer', background: '#eef2ff', color: '#29327b', textDecoration: 'none',
};

function RequestCard({id, data}: {id: string; data: any}) {
    const toast = useToast();
    const [note, setNote] = useState(data.adminNote ?? '');
    useEffect(() => setNote(data.adminNote ?? ''), [data.adminNote]);
    const status: Status = data.status ?? 'open';

    const update = async (fields: any, okMsg: string) => {
        try {
            await updateDoc(doc(db, 'supportRequests', id), {
                ...fields,
                updatedAt: serverTimestamp(),
                updatedBy: getAuth(Firebase).currentUser?.email ?? null,
            });
            toast({position: 'top', title: okMsg, status: 'success', duration: 1500});
        } catch (e: any) {
            toast({position: 'top', title: e.message, status: 'error', duration: 3000});
        }
    };

    return (
        <div style={{border: '1px solid #eee', borderRadius: 10, padding: 14, marginBottom: 12, background: '#fff'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap'}}>
                <div>
                    <div style={{fontWeight: 700, fontSize: 14}}>
                        {data.subject} <Tag color={STATUS_COLORS[status]} style={{marginLeft: 6}}>{status}</Tag>
                    </div>
                    <div style={{fontSize: 12, color: '#666', marginTop: 2}}>
                        {data.name} · {data.contact} · {fmtDate(data.createdAt)} · {data.source === 'android' ? 'App' : 'Web'}
                        {data.appVersion ? ` ${data.appVersion}` : ''}
                        {data.userId ? <> · <Link to={`/buyers/${data.userId}`}>account</Link></> : ' · signed out'}
                    </div>
                </div>
                <div style={{display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap'}}>
                    {contactLinks(data.contact).map((l) => (
                        <a key={l.label} href={l.href} target="_blank" rel="noreferrer" style={btn}>{l.label}</a>
                    ))}
                </div>
            </div>

            <div style={{whiteSpace: 'pre-wrap', fontSize: 13.5, color: '#222', margin: '10px 0', lineHeight: 1.45}}>
                {data.message}
            </div>

            <div style={{display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap'}}>
                <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Internal note (only admins see this)"
                    style={{flex: 1, minWidth: 220, padding: '5px 8px', borderRadius: 8, border: '1px solid #eee', fontSize: 13}}
                />
                <button style={btn} onClick={() => update({adminNote: note.trim()}, 'Note saved')}>Save note</button>
                {status !== 'replied' && (
                    <button style={btn} onClick={() => update({status: 'replied'}, 'Marked replied')}>Mark replied</button>
                )}
                {status !== 'closed' && (
                    <button style={{...btn, background: '#f5f5f5', borderColor: '#ddd', color: '#555'}}
                            onClick={() => update({status: 'closed'}, 'Closed')}>Close</button>
                )}
                {status === 'closed' && (
                    <button style={btn} onClick={() => update({status: 'open'}, 'Reopened')}>Reopen</button>
                )}
            </div>
            {data.updatedBy && (
                <div style={{fontSize: 11, color: '#aaa', marginTop: 6}}>
                    Last updated by {data.updatedBy} · {fmtDate(data.updatedAt)}
                </div>
            )}
        </div>
    );
}

function FormsTab() {
    const [filter, setFilter] = useState<Status | 'all'>('open');
    const [snap, loading, error] = useCollection(query(collection(db, 'supportRequests'), orderBy('createdAt', 'desc')));
    const docs = useMemo(() => snap?.docs ?? [], [snap]);
    const counts = useMemo(() => {
        const c: Record<string, number> = {open: 0, replied: 0, closed: 0, all: docs.length};
        docs.forEach((d) => { c[d.data().status ?? 'open'] = (c[d.data().status ?? 'open'] ?? 0) + 1; });
        return c;
    }, [docs]);
    const shown = docs.filter((d) => filter === 'all' || (d.data().status ?? 'open') === filter);

    return (
        <div>
            <div style={{display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap'}}>
                {(['open', 'replied', 'closed', 'all'] as const).map((f) => (
                    <button key={f} onClick={() => setFilter(f)}
                            style={{...btn, background: filter === f ? '#29327b' : '#eef2ff', color: filter === f ? '#fff' : '#29327b'}}>
                        {f[0].toUpperCase() + f.slice(1)} ({counts[f] ?? 0})
                    </button>
                ))}
            </div>
            <div style={{fontSize: 12, color: '#888', marginBottom: 12}}>
                Messages from the Contact form (signed-out visitors and anyone who used the form). Reply by email,
                call or WhatsApp using the contact they left, then mark it replied or close it.
            </div>
            {loading && <div style={{padding: 30, color: '#999'}}>Loading…</div>}
            {error && <div style={{padding: 30, color: '#b91c1c'}}>{error.message}</div>}
            {!loading && shown.length === 0 && <div style={{padding: 30, color: '#999'}}>Nothing here.</div>}
            {shown.map((d) => <RequestCard key={d.id} id={d.id} data={d.data()}/>)}
        </div>
    );
}

const Support = () => {
    const [collapsed, setCollapsed] = useState(false);
    return (
        <Layout style={{minHeight: '100vh'}}>
            <Headerbar collapsed={collapsed} setCollapsed={setCollapsed}/>
            <Layout className="site-layout">
                <Sidebar collapsed={collapsed}/>
                <Content>
                    <Container className="mt-2">
                        <Card className="my-2 p-3 font-JosefinSans">
                            <Text h4 size={23} css={{color: "#29327b", fontWeight: "$bold"}}>Support</Text>
                            <Tabs defaultActiveKey="chats">
                                <TabPane tab="Chats" key="chats"><SupportChats/></TabPane>
                                <TabPane tab="Tickets" key="tickets"><SupportTickets/></TabPane>
                                <TabPane tab="Support payments" key="payments"><SupportPayments/></TabPane>
                                <TabPane tab="Forms" key="forms"><FormsTab/></TabPane>
                            </Tabs>
                        </Card>
                    </Container>
                </Content>
            </Layout>
        </Layout>
    );
};

export default Support;
